import { Component, EventEmitter, OnInit, Output } from '@angular/core';
import { combineAll, DcBaseComponent } from '@datachain/ui-sdk/common';
import {
	firstValueFrom,
	map,
	Observable,
	ReplaySubject,
	takeUntil,
	tap,
} from 'rxjs';

import { DcIcons } from '../../app-dc.icons';
import { PermissionConfigItemEntity } from './permission-config-item.entity';
import {
	PermissionConfigListEntity,
	Permission,
	PermissionConfig,
} from './permission-config-list.entity';

export type Configuration = {
	usePanel: boolean;
	columnWidth: number;
	inViewMode: boolean;
};

@Component({
	selector: 'app-permissions-config',
	templateUrl: './app-permissions-config.component.html',
	styleUrls: ['./app-permissions-config.component.scss'],
	inputs: ['permissionsConfig', 'config'],
})
export class AppPermissionsConfigComponent
	extends DcBaseComponent
	implements OnInit
{
	protected readonly EligiblePermissions = Permission;
	protected readonly DcIcons = DcIcons;

	@Output()
	public updatedPermissionConfig = new EventEmitter<PermissionConfigListEntity>();

	public vo$: Observable<{
		columns: Array<PermissionConfig>;
		elements: Array<PermissionConfigItemEntity>;
		list: PermissionConfigListEntity;
		config: Configuration;
	}>;

	private readonly permissionsConfigList$ =
		new ReplaySubject<PermissionConfigListEntity>(1);

	public constructor() {
		super();
		this.cmpId = 'app-permissions-config';

		this.vo$ = combineAll({
			columns: this.permissionsConfigList$.pipe(
				map((list) => Array.from(list.availablePermissions.values()))
			),
			elements: this.permissionsConfigList$.pipe(
				map((list) => Array.from(list.elements.values()))
			),
			list: this.permissionsConfigList$,
			config: this.toObservable<Configuration>('config'),
		});
	}

	public ngOnInit(): void {
		super.ngOnInit();

		this.toObservable<PermissionConfigListEntity>('permissionsConfig')
			.pipe(
				takeUntil(this.onDestroy$),
				tap((list) => {
					this.permissionsConfigList$.next(list);
				})
			)
			.subscribe();

		this.permissionsConfigList$
			.pipe(
				takeUntil(this.onDestroy$),
				tap((list) => {
					this.updatedPermissionConfig.next(list);
				})
			)
			.subscribe();
	}

	public async onToggleChange(
		key: number,
		permission: Permission,
		checked: boolean
	): Promise<void> {
		const currentList = await firstValueFrom(this.permissionsConfigList$);
		const elem = currentList.elements.get(`${key}`);

		if (!elem) {
			return;
		}

		elem.updatePermission(checked, permission, currentList.activatePermissions);

		currentList
			.updateElement(`${key}`, elem)
			.updatePermissionsConfig(permission);

		this.permissionsConfigList$.next(currentList);
	}

	public async onRowToggle(key: number, checked: boolean): Promise<void> {
		const currentList = await firstValueFrom(this.permissionsConfigList$);
		const elem = currentList.elements.get(`${key}`);

		if (!elem) {
			return;
		}

		const availablePermissions = checked ? currentList.activatePermissions : [];

		elem.updatePermissions(availablePermissions);

		currentList.updateElement(`${key}`, elem).checkIfAllSelected();

		this.permissionsConfigList$.next(currentList);
	}

	public async onColumnToggle(
		permission: Permission,
		checked: boolean
	): Promise<void> {
		const currentList = await firstValueFrom(this.permissionsConfigList$);

		currentList.updateAllElements(checked, permission);

		currentList.updatePermissionsConfig(permission);

		this.permissionsConfigList$.next(currentList);
	}
}
